import { MutationObserver } from "@tanstack/react-query";

import { isOfflineQueued } from "../api/client";
import { Topic, changed } from "./changes";
import { queryClient } from "./queryClient";
import { showToast } from "./toast";

// Saves that finish behind the page, as TanStack Query mutations (ADR 0055).
// The page shows the change at once and moves on; the write belongs to the
// query client, not the component that made it, so leaving the page keeps
// it. Only unloading the page (a reload, closing the tab) would drop it, so
// the browser asks first while any is out. Saves to one thing (`scope`) wait
// for the one before, so the last made is the one that stays. Once the last
// is in, every page showing what they changed refetches it, so one you've
// come back to while a save was out shows it too.

const mutations = queryClient.getMutationCache();

const holdUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = "";
};

// What the saves out since the last went in change: a list's (`list:<id>`)
// change its lists; the rest, the shelf.
const touched = new Set<Topic>();

let out = false;
mutations.subscribe((event) => {
  if (event.type === "added") {
    // `save` gives every one its scope.
    const { id: scope } = event.mutation.options.scope as { id: string };
    touched.add(scope.startsWith("list:") ? "lists" : "shelf");
  }
  const now = queryClient.isMutating() > 0;
  if (now === out) return;
  out = now;
  if (now) {
    window.addEventListener("beforeunload", holdUnload);
    return;
  }
  window.removeEventListener("beforeunload", holdUnload);
  const topics = Array.from(touched);
  touched.clear();
  changed(...topics);
});

/** Send `send` behind the page, after any save to `scope` still out. */
export const save = <T>(scope: string, send: () => Promise<T>): Promise<T> =>
  new MutationObserver<T, unknown, void>(queryClient, {
    mutationFn: send,
    scope: { id: scope },
    // Offline, the client queues a shelf change itself (src/api/offline.ts)
    // and anything else fails at once, whatever the browser says it is.
    networkMode: "always",
  }).mutate();

/** What the page saying a change is saved offline shows. */
export const SAVED_OFFLINE =
  "Saved offline — will sync when you're back online.";

/** Whether `err` is a shelf change the client kept to send once online. */
export const savedOffline = isOfflineQueued;

type Change<T> = {
  // Saves to one thing go in order (`save`).
  scope: string;
  send: () => Promise<T>;
  // What the change is, as "Couldn't save <failed>." says: "your review".
  failed: string;
  // The change shown at once, and put back if the save fails.
  show?: () => void;
  putBack?: () => void;
  // The API's answer, once in.
  saved?: (answer: T) => void;
  // Kept offline, to send once back: a toast says so, unless the page does.
  offline?: () => void;
};

/**
 * A change the page shows at once and saves behind it (`save`). If it fails,
 * the page puts back what was there, and a toast says so with Retry, as the
 * page may be gone by then.
 */
export const change = <T>(c: Change<T>): Promise<void> => {
  c.show?.();
  return save(c.scope, c.send).then(c.saved, (err) => {
    if (savedOffline(err)) {
      (c.offline ?? (() => showToast(SAVED_OFFLINE)))();
      return;
    }
    c.putBack?.();
    showToast(`Couldn't save ${c.failed}.`, {
      label: "Retry",
      run: () => {
        void change(c);
      },
    });
  });
};
